import { cancel, select, takeEvery } from 'redux-saga/effects';
import {
  DruckRequestPayload,
  getGeschaeftsart,
  GlobalAction,
  GlobalActions,
  GlobalPut,
} from '@ps20-frontend/shop-integration-redux';
import {
  EinzelleistungDruck,
  Feature,
  SpartenType,
  Tiergeschlecht,
  TkvDiagnoseDruck,
  TkvDruckdaten,
  TkvVersichertesTierDruck,
  ZusatzoptionenDruck,
} from '@ps20/bff-models';
import {
  App,
  LeistungEigenschaft,
  mapLeistungen,
  mapToBffVorvertragsdatenDruck,
  mapZusatzoptionenDruck,
  numberToGermanNumberFormattedString,
  Produktlinie,
  TierartEnum,
} from '@ps20-frontend/frontend-models';
import { PS20Logger } from '@ps20-frontend/logging';
import dayjs from 'dayjs';
import { getProduktlinienDruck, mapGefahren } from '@ps20-frontend/druckdaten-lib';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { DATEFORMAT_LOCAL_STATE, DATEFORMAT_SHORT_ISO } from '@ps20-frontend/utils';
import { mapGesamtbeitragIfJaehrlichAndBeitragIsWrong } from '@ps20-frontend/preis-lib';
import { selectFeatureToggle } from '@ps20-frontend/selector';
import { GeschlechtEnum, Versicherungsnehmer } from '../reducers/versicherungsnehmer.reducer';
import {
  selectSelectedProduktlinie,
  selectZusatzoptionItems,
} from '../../app/shared/selectors/produkt.selector';
import { IAppState } from '../appState';
import {
  convertToBffTierart,
  convertToBffTierhaltung,
  mapSublimit,
} from './transformer/tkvData.transformer';

export function* watchDruckdatenChanged() {
  yield takeEvery(GlobalActions.DRUCKDATEN_REQUESTED, safeSaga(onDruckdatenRequested, App.TKV));
}

export function* onDruckdatenRequested(action: GlobalAction<DruckRequestPayload>) {
  try {
    const state: IAppState = yield select();
    const appIds: string[] = action.payload.appIds;

    if (!appIds.includes(App.TKV)) {
      yield cancel();
      return;
    }

    const tkvDruckdaten: TkvDruckdaten = mapDruckdaten(state);
    GlobalPut.getInstance().put(new GlobalActions().druckdatenGenerated(tkvDruckdaten));
  } catch (e) {
    PS20Logger.error(App.TKV, e as Error, 'Druckdatensaga.onDruckdatenRequested()');
    yield cancel();
  }
}

function mapDruckdaten(state: IAppState): TkvDruckdaten {
  const ausgewaehlteProduktlinie: Produktlinie = selectSelectedProduktlinie(state);
  const zusatzoptionenDruck: Array<ZusatzoptionenDruck> = mapZusatzoptionenDruck(
    selectZusatzoptionItems(state),
  );
  const anzahlOperationen: number = state.versicherungsnehmer.anzahlOperationen;
  const heilbehandlungenVorhanden: boolean = state.versicherungsnehmer.behandlungen.ohneOperation;
  const vorteilsRabattActivated = selectFeatureToggle(state, Feature.VORTEILSRABATT);
  return {
    zusatzoptionenDruck,
    angebotsId: state.angebotsId,
    antragsnummer: state.antragsnummer,
    longText: ausgewaehlteProduktlinie.langtext,
    shortText: ausgewaehlteProduktlinie.kurztext,
    rbdAktuell: state.taasInfo.rbdNachlass,
    vorteilsRabatt: vorteilsRabattActivated ? state.taasInfo.vorteilsrabatt || 0 : undefined,
    angebotsdatum: vorteilsRabattActivated ? state.vertragsoptionen.angebotsdatum : undefined,
    ablauf: null,
    abweichungVomZielmarkt: false,
    beginn: null,
    gesamtbeitrag: numberToGermanNumberFormattedString(
      mapGesamtbeitragIfJaehrlichAndBeitragIsWrong(
        state.vertragsoptionen.zahlungsweise,
        state.gesamtbeitragMitRabatt,
        state.jahresbeitrag,
        App.TKV,
      ),
    ),
    versichertesTier: getVersichertesTierDataFromStrecke(state.versicherungsnehmer),
    leistungen: mapLeistungenForTkv(ausgewaehlteProduktlinie),
    produktlinien: getProduktlinienDruck(state.produkt),
    selbstbeteiligung: state.vertragsoptionen.selbstbeteiligung,
    spartenType: SpartenType.TIERKRANKEN,
    zaehlstuecke: state.rabattstufe,
    zuersGefaehrdungsklasse: '',
    treuebonus: state.taasInfo.tbNachlass,
    gefahrenDruck: mapGefahren(state.produkt),
    vorvertragsnummern: mapToBffVorvertragsdatenDruck(state.vertragsoptionen.vorvertragsNummern),
    vorschaedenDruck: {
      vorhanden: (anzahlOperationen != null && anzahlOperationen > 0) || heilbehandlungenVorhanden,
      tkvOperation: anzahlOperationen,
      tkvHeilkosten: heilbehandlungenVorhanden ? 999 : 0,
    },
    sublimit: mapSublimit(
      state.produkt.produktlinien.find(produktlinie => produktlinie.selected).zusatzoptionen[0]
        .eigenschaft,
    ),
    beibehalten: state.vertragsoptionen.beibehalten,
    kooperation: state.vertragsoptionen.selectedKooperation,
    geschaeftsart: getGeschaeftsart(state.vertragsoptionen),
  };
}

function mapLeistungenForTkv(ausgewaehlteProduktlinie: Produktlinie): Array<EinzelleistungDruck> {
  return mapLeistungen(
    {
      ...ausgewaehlteProduktlinie,
      leistungen: ausgewaehlteProduktlinie.leistungen.filter(
        leistung => leistung.eigenschaft !== LeistungEigenschaft.EXKLUSIVE,
      ),
    },
    SpartenType.TIERKRANKEN,
  );
}

function getVersichertesTierDataFromStrecke(
  versicherungsnehmer: Versicherungsnehmer,
): TkvVersichertesTierDruck {
  if (versicherungsnehmer.tierart === TierartEnum.KATZE) {
    return {
      isKastriert: versicherungsnehmer.isTierKastriert,
      geburtsdatum: dayjs(versicherungsnehmer.geburtsdatumTier, DATEFORMAT_LOCAL_STATE).format(
        DATEFORMAT_SHORT_ISO,
      ),
      geschlecht:
        versicherungsnehmer.geschlecht === GeschlechtEnum.MAENNLICH
          ? Tiergeschlecht.MAENNLICH
          : Tiergeschlecht.WEIBLICH,
      tierhaltung: convertToBffTierhaltung(versicherungsnehmer.katzenhaltung),
      tierart: convertToBffTierart(versicherungsnehmer.tierart),
      rasse: versicherungsnehmer.rasse.value,
      hasHeilbehandlungen: versicherungsnehmer.behandlungen?.ohneOperation,
      tiername: null,
      kennzeichnungsArt: null,
      kennzeichnung: null,
      istBeiTierarzt: null,
      einsatzgebiet: null,
      tkvDiagnoseDruckList: mapTkvDiagnoseDruck(versicherungsnehmer),
      isJungTierActive: true,
    };
  }
  return {
    isKastriert: versicherungsnehmer.isTierKastriert,
    geburtsdatum: dayjs(versicherungsnehmer.geburtsdatumTier, DATEFORMAT_LOCAL_STATE).format(
      DATEFORMAT_SHORT_ISO,
    ),
    geschlecht:
      versicherungsnehmer.geschlecht === GeschlechtEnum.MAENNLICH
        ? Tiergeschlecht.MAENNLICH
        : Tiergeschlecht.WEIBLICH,
    tierart: convertToBffTierart(versicherungsnehmer.tierart),
    rasse: versicherungsnehmer.rasse.value,
    hasHeilbehandlungen: versicherungsnehmer.behandlungen?.ohneOperation,
    tiername: null,
    kennzeichnungsArt: null,
    kennzeichnung: null,
    istBeiTierarzt: null,
    einsatzgebiet: null,
    tkvDiagnoseDruckList: mapTkvDiagnoseDruck(versicherungsnehmer),
    isJungTierActive: versicherungsnehmer.tierart === TierartEnum.HUND,
  };
}

function mapTkvDiagnoseDruck(versicherungsnehmer: Versicherungsnehmer): TkvDiagnoseDruck[] {
  return versicherungsnehmer?.diagnose && versicherungsnehmer?.diagnose.length > 0
    ? versicherungsnehmer?.diagnose.map(diagnose => ({
        gewaehlteDiagnose: diagnose.diagnose,
        gewaehlteDiagnoseDetail: diagnose.diagnoseDetail,
        diagnoseBeschreibung: diagnose.diagnoseBeschreibung,
      }))
    : [];
}
